<template>
  <div class="ai-customization-panel">
    <div class="ai-customization-panel__header">
      <div class="ai-customization-panel__header-left">
        <Icon icon="mdi:sparkles" class="ai-customization-panel__icon" />
        <span class="ai-customization-panel__title">{{ title }}</span>
      </div>
      <button
        v-if="showClose"
        type="button"
        class="ai-customization-panel__close"
        @click="emit('close')"
        aria-label="Close"
      >
        <Icon icon="mdi:close" />
      </button>
    </div>
    <div class="ai-customization-panel__body">
      <div class="ai-customization-panel__field">
        <label class="ai-customization-panel__label">Custom Instructions</label>
        <textarea
          v-model="customInstructions"
          class="ai-customization-panel__textarea"
          placeholder="E.g., 'Mention our upcoming product launch' or 'Keep it brief and to the point'."
          rows="3"
        />
      </div>

      <!-- File Attachments -->
      <div v-if="showAttachments" class="ai-customization-panel__attachments">
        <label class="ai-customization-panel__label">Attachments</label>
        <div
          class="ai-customization-panel__dropzone"
          :class="{ 'ai-customization-panel__dropzone--dragover': isDragOver }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="triggerFileInput"
        >
          <Icon icon="mdi:cloud-upload-outline" class="ai-customization-panel__dropzone-icon" />
          <span class="ai-customization-panel__dropzone-text">
            Drop files here or <span class="ai-customization-panel__dropzone-link">browse</span>
          </span>
          <span class="ai-customization-panel__dropzone-hint">
            Max {{ maxFiles }} files, {{ maxFileSizeMB }}MB each
          </span>
        </div>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="ai-customization-panel__file-input"
          @change="handleFileSelect"
        />
        <ul v-if="attachments.length > 0" class="ai-customization-panel__file-list">
          <li
            v-for="(file, index) in attachments"
            :key="`${file.name}-${index}`"
            class="ai-customization-panel__file-item"
          >
            <Icon icon="mdi:file-outline" class="ai-customization-panel__file-icon" />
            <span class="ai-customization-panel__file-name">{{ file.name }}</span>
            <span class="ai-customization-panel__file-size">{{ formatFileSize(file.size) }}</span>
            <button
              type="button"
              class="ai-customization-panel__file-remove"
              @click="removeFile(index)"
              aria-label="Remove file"
            >
              <Icon icon="mdi:close-circle" />
            </button>
          </li>
        </ul>
      </div>

      <Button
        variant="primary"
        size="sm"
        icon="mdi:refresh"
        :loading="disabled"
        :disabled="disabled"
        class="ai-customization-panel__generate-btn"
        @click="handleGenerate"
      >
        {{ generateButtonLabel }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { ref } from "vue";
import { useToast } from "@/composables/ui/useToast";
import Button from "@/components/ui/Button.vue";

interface Props {
  disabled?: boolean;
  title?: string;
  generateButtonLabel?: string;
  showClose?: boolean;
  showAttachments?: boolean;
  maxFiles?: number;
  maxFileSizeMB?: number;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  title: "AI Refine",
  generateButtonLabel: "Generate Message",
  showClose: false,
  showAttachments: true,
  maxFiles: 3,
  maxFileSizeMB: 5,
});

const $toast = useToast();

const customInstructions = ref("");
const attachments = ref<File[]>([]);
const isDragOver = ref(false);
const fileInputRef = ref<HTMLInputElement | null>(null);

const emit = defineEmits<{
  generate: [payload: { customInstructions: string; attachments: File[] }];
  close: [];
}>();

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateAndAddFiles = (files: FileList | File[]) => {
  const maxBytes = props.maxFileSizeMB * 1024 * 1024;
  const remaining = props.maxFiles - attachments.value.length;

  if (remaining <= 0) {
    $toast?.info(`Maximum ${props.maxFiles} files allowed`, {
      position: "top-right",
      duration: 3000,
    });
    return;
  }

  const filesToAdd = Array.from(files).slice(0, remaining);

  for (const file of filesToAdd) {
    if (file.size > maxBytes) {
      $toast?.error(`"${file.name}" exceeds ${props.maxFileSizeMB}MB limit`, {
        position: "top-right",
        duration: 3000,
      });
      continue;
    }
    attachments.value.push(file);
  }
};

const handleDrop = (event: DragEvent) => {
  isDragOver.value = false;
  if (event.dataTransfer?.files) {
    validateAndAddFiles(event.dataTransfer.files);
  }
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    validateAndAddFiles(input.files);
  }
  // Reset input so re-selecting the same file triggers change
  input.value = "";
};

const removeFile = (index: number) => {
  attachments.value.splice(index, 1);
};

const handleGenerate = () => {
  emit("generate", {
    customInstructions: customInstructions.value.trim(),
    attachments: [...attachments.value],
  });
};
</script>

<style scoped>
.ai-customization-panel {
  width: 100%;
  max-width: 480px !important;
  min-width: 360px !important;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  border: 1px solid #e0e0ea;
  overflow: hidden;
}

.ai-customization-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #e8e8f0;
  background: #fafafa;
}

.ai-customization-panel__header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ai-customization-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.ai-customization-panel__close:hover {
  background: rgba(107, 114, 128, 0.12);
  color: #374151;
}

.ai-customization-panel__icon {
  font-size: 1rem;
  color: #4f46e5;
}

.ai-customization-panel__title {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #1a1a2e;
}

.ai-customization-panel__body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 180px;
}

.ai-customization-panel__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ai-customization-panel__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
}

.ai-customization-panel__textarea {
  width: 100%;
  min-height: 72px;
  padding: 8px 10px;
  font-size: 0.8125rem;
  font-family: inherit;
  color: #1a1a2e;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  resize: vertical;
  transition: border-color 0.2s;
}

.ai-customization-panel__textarea:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
}

.ai-customization-panel__textarea::placeholder {
  color: #9ca3af;
}

.ai-customization-panel__generate-btn {
  width: 100%;
}

/* Attachments */
.ai-customization-panel__attachments {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-customization-panel__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 12px;
  border: 1.5px dashed #d1d5db;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-customization-panel__dropzone:hover {
  border-color: #4f46e5;
  background: #f5f3ff;
}

.ai-customization-panel__dropzone--dragover {
  border-color: #4f46e5;
  background: #ede9fe;
}

.ai-customization-panel__dropzone-icon {
  font-size: 1.5rem;
  color: #9ca3af;
}

.ai-customization-panel__dropzone-text {
  font-size: 0.75rem;
  color: #6b7280;
}

.ai-customization-panel__dropzone-link {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: underline;
}

.ai-customization-panel__dropzone-hint {
  font-size: 0.6875rem;
  color: #9ca3af;
}

.ai-customization-panel__file-input {
  display: none;
}

.ai-customization-panel__file-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ai-customization-panel__file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
}

.ai-customization-panel__file-icon {
  font-size: 1rem;
  color: #6b7280;
  flex-shrink: 0;
}

.ai-customization-panel__file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #374151;
  font-weight: 500;
}

.ai-customization-panel__file-size {
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 0.6875rem;
}

.ai-customization-panel__file-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.15s;
}

.ai-customization-panel__file-remove:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
}
</style>
